/**
 * Picking several of the user's projects at once (ProjectsToFoldersPanel /
 * IconProjectToFolders.prefab): a red-topped panel with Close on the left and
 * Done on the right, and a grid of project cards (the template's home picture,
 * the name on a translucent white band); a picked card turns paler and shows
 * a red tick. Used by "Add Projects" in a folder and by "Choose projects" in a
 * classroom.
 */
import { useEffect, useState } from "react";
import type { ProjectSummary } from "../data/myProjects";
import { t } from "../player/i18n";
import { Icon, SearchBox, matchesSearch, templateArt } from "./widgets";

export function ProjectCardPicker({
  title,
  projects,
  initial = [],
  onDone,
  onClose,
}: {
  title: string;
  projects: ProjectSummary[];
  /** already picked when the panel opens */
  initial?: string[];
  /** saves the picked ids (the panel closes when it resolves) */
  onDone: (ids: string[]) => Promise<unknown>;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial));
  const [q, setQ] = useState("");
  const shown = projects.filter((p) => matchesSearch(p.name, q));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const done = async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      await onDone(projects.filter((p) => picked.has(p.id)).map((p) => p.id));
      onClose();
    } catch {
      setError(true);
      setBusy(false);
    }
  };
  return (
    <div className="modal-veil" onClick={onClose}>
      <div className="cardpick" role="dialog" aria-modal aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="cardpick-top">
          <button className="cardpick-btn" onClick={onClose}>
            {t("close")}
          </button>
          <span className="cardpick-title">{title}</span>
          <button className="cardpick-btn" onClick={() => void done()} disabled={busy}>
            {busy ? "…" : t("done")}
          </button>
        </div>
        <div className="cardpick-body">
          {error && <p className="error">{t("errSave")}</p>}
          {projects.length > 12 && (
            <div className="cardpick-search">
              <SearchBox value={q} onChange={setQ} />
              {picked.size > 0 && <span className="muted small">✓ {picked.size}</span>}
            </div>
          )}
          {shown.length === 0 ? (
            <p className="muted">{q.trim() ? t("noSearchResults") : t("noProjectsTitle")}</p>
          ) : (
            <div className="cardpick-grid">
              {shown.map((p) => {
                const on = picked.has(p.id);
                return (
                  <button key={p.id} className={`cardpick-card ${on ? "on" : ""}`} style={templateArt(p.template)} onClick={() => toggle(p.id)} aria-pressed={on}>
                    <span className="cardpick-band">
                      <span className="cardpick-name" dir="auto">
                        {p.name}
                      </span>
                    </span>
                    {on && (
                      <span className="cardpick-sel">
                        <Icon name="tick" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
